export const CONTEXT_OPERATOR_KEYS = [
  'equals',
  'in',
  'contains',
  'starts_with',
  'ends_with',
  'matches_regex',
  'contains_all',
  'within',
  'within_any'
] as const;
export type ContextOperatorKey = (typeof CONTEXT_OPERATOR_KEYS)[number];

export const CONTEXT_PREDICATE_TYPES = ['identity', 'string', 'labels', 'ip'] as const;
export type ContextPredicateType = (typeof CONTEXT_PREDICATE_TYPES)[number];

export const CONTEXT_OPERAND_TYPES = ['string', 'ip', 'cidr'] as const;
export type ContextOperandType = (typeof CONTEXT_OPERAND_TYPES)[number];

export const OPERATORS_BY_PREDICATE_TYPE: Record<ContextPredicateType, readonly ContextOperatorKey[]> = {
  identity: ['equals', 'in'],
  string: ['equals', 'in', 'contains', 'starts_with', 'ends_with', 'matches_regex'],
  labels: ['equals', 'in', 'contains_all'],
  ip: ['equals', 'in', 'within', 'within_any']
};

export interface ContextOperatorCapability {
  key: ContextOperatorKey;
  operand: { type: ContextOperandType; multiple: boolean };
}

export interface ContextFieldCapability {
  /** Semantic field identifier, matching the proto field name (e.g. `device_name`). */
  key: string;
  predicateType: ContextPredicateType;
  supportsNegation: boolean;
  operators: ContextOperatorCapability[];
}

export interface ContextNetworkScopeCapability {
  value: string;
}

export interface ContextFilterCapabilities {
  version: 1;
  networkScopes: ContextNetworkScopeCapability[];
  fields: ContextFieldCapability[];
}

export const CONTEXT_CAPABILITIES_VERSION = 1;

export const CONTEXT_NETWORK_SCOPE_ANY = 'NETWORK_SCOPE_ANY';

export class ContextCapabilitiesError extends Error {
  constructor(message: string) {
    super(`Invalid context filter capabilities: ${message}`);
    this.name = 'ContextCapabilitiesError';
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const requireKey = (value: unknown, what: string): string => {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new ContextCapabilitiesError(`${what} must be a nonempty string`);
  }

  return value;
};

function validateOperator(raw: unknown, field: ContextFieldCapability, seen: Set<string>): ContextOperatorCapability {
  if (!isRecord(raw)) {
    throw new ContextCapabilitiesError(`operator of "${field.key}" must be an object`);
  }

  const key = raw.key as ContextOperatorKey;

  if (!CONTEXT_OPERATOR_KEYS.includes(key)) {
    throw new ContextCapabilitiesError(`unsupported operator "${String(raw.key)}" on "${field.key}"`);
  }

  if (seen.has(key)) {
    throw new ContextCapabilitiesError(`duplicate operator "${key}" on "${field.key}"`);
  }

  seen.add(key);

  if (!OPERATORS_BY_PREDICATE_TYPE[field.predicateType].includes(key)) {
    throw new ContextCapabilitiesError(`operator "${key}" is not valid for a ${field.predicateType} field`);
  }

  if (!isRecord(raw.operand)) {
    throw new ContextCapabilitiesError(`operator "${key}" on "${field.key}" is missing its operand`);
  }

  const operandType = raw.operand.type as ContextOperandType;

  if (!CONTEXT_OPERAND_TYPES.includes(operandType)) {
    throw new ContextCapabilitiesError(`operator "${key}" on "${field.key}" has an unsupported operand type`);
  }

  if (typeof raw.operand.multiple !== 'boolean') {
    throw new ContextCapabilitiesError(`operator "${key}" on "${field.key}" must declare operand cardinality`);
  }

  return {
    key,
    operand: { type: operandType, multiple: raw.operand.multiple }
  };
}

function validateField(raw: unknown, seen: Set<string>): ContextFieldCapability {
  if (!isRecord(raw)) {
    throw new ContextCapabilitiesError('each field must be an object');
  }

  const key = requireKey(raw.key, 'field key');

  if (seen.has(key)) {
    throw new ContextCapabilitiesError(`duplicate field "${key}"`);
  }

  seen.add(key);

  const predicateType = raw.predicateType as ContextPredicateType;

  if (!CONTEXT_PREDICATE_TYPES.includes(predicateType)) {
    throw new ContextCapabilitiesError(`unsupported predicate type on "${key}"`);
  }

  if (typeof raw.supportsNegation !== 'boolean') {
    throw new ContextCapabilitiesError(`"${key}" must declare negation support`);
  }

  if (!Array.isArray(raw.operators) || raw.operators.length === 0) {
    throw new ContextCapabilitiesError(`"${key}" must offer at least one operator`);
  }

  const field: ContextFieldCapability = {
    key,
    predicateType,
    supportsNegation: raw.supportsNegation,
    operators: []
  };

  const seenOperators = new Set<string>();
  field.operators = raw.operators.map((operator) => validateOperator(operator, field, seenOperators));

  return field;
}

export function parseContextFilterCapabilities(raw: unknown): ContextFilterCapabilities {
  if (!isRecord(raw)) {
    throw new ContextCapabilitiesError('response must be an object');
  }

  if (raw.version !== CONTEXT_CAPABILITIES_VERSION) {
    throw new ContextCapabilitiesError(`unsupported version ${String(raw.version)}`);
  }

  if (!Array.isArray(raw.networkScopes)) {
    throw new ContextCapabilitiesError('networkScopes must be an array');
  }

  if (!Array.isArray(raw.fields)) {
    throw new ContextCapabilitiesError('fields must be an array');
  }

  const seenScopes = new Set<string>();
  const networkScopes = raw.networkScopes.map((scope) => {
    if (!isRecord(scope)) {
      throw new ContextCapabilitiesError('each network scope must be an object');
    }

    const value = requireKey(scope.value, 'network scope value');

    if (seenScopes.has(value)) {
      throw new ContextCapabilitiesError(`duplicate network scope "${value}"`);
    }

    seenScopes.add(value);

    return { value };
  });

  const seenFields = new Set<string>();
  const fields = raw.fields.map((field) => validateField(field, seenFields));

  if (fields.length > 0 && !seenScopes.has(CONTEXT_NETWORK_SCOPE_ANY)) {
    throw new ContextCapabilitiesError(`a nonempty catalog must support ${CONTEXT_NETWORK_SCOPE_ANY}`);
  }

  return { version: CONTEXT_CAPABILITIES_VERSION, networkScopes, fields };
}
